import Vue from 'vue';
import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import moment from 'moment';

interface AgentDetailsState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;

    document: any;

    meta: any;
}

const state: AgentDetailsState = {
    loading: false,
    loaded: false,
    fatalError: false,

    document: null,

    meta: {},
};

const initState = JSON.parse(JSON.stringify(state));

export const AGENT_DATA = 'GET_AGENT_DATA';

export const actions: ActionTree<AgentDetailsState, RootState> = {
    reset({ commit }) {
        commit('RESET_STATE')
    },

    loadData({ commit, getters, dispatch, state }, params) {
        const vm = (this as any)._vm;

        if (params.id == 'new') {
            state.document = {};
            state.loaded = true
        } else {
            return new Promise((resolve, reject) => {
                if (!params.silence) {
                    commit('START_LOADING');
                }

                const columns: any = {
                    id: 'agent.id',
                    name: 'agent.name',
                    code_1s: 'agent.code_1s',

                    created_at: 'agent.created_at',
                    updated_at: 'agent.updated_at',
                    archived_at: 'agent.archived_at',

                    category_income: 'agent.category_income',
                    category_profit: 'agent.category_profit',
                    category_activity: 'agent.category_activity',

                    manager_id: 'agent.manager_id',
                    manager_name: 'primary_manager.name',

                    secondary_manager_id: 'agent.secondary_manager_id',
                    secondary_manager_name: 'secondary_manager.name',

                    delivery_warehouse_id: 'agent.delivery_warehouse_id',

                    contacts: 'agent.contacts',
                    primary_contact_type: 'agent.primary_contact_type',

                    credit: 'agent.credit',
                    competitor_trademark: 'agent.competitor_trademark',
                    payment_deferment_period_id: 'agent.payment_deferment_period_id',
                    source: 'agent.source',

                    city_name: 'city.name',

                    payment_type_id: 'ptype.id',
                    payment_type_name: 'ptype.name',

                    entity_name: 'ent.name',

                    type_id: 'agent.type_id',
                    type_code: 'ctype.code',
                    special_orders: 'agent.special_orders'
                };

                vm.$db([
                    vm.$q(AGENT_DATA)
                    .raw('documents.list')
                    .action({
                        columns,
                        from: 's:agents',
                        from_as: 'agent',
                        joins: [
                          {
                            join: 's:employees',
                            type: 'left',
                            as:   'primary_manager',
                            on: {
                              $eq: ['primary_manager.id', 'agent.manager_id']
                            }
                          },
                          {
                            join: 's:employees',
                            type: 'left',
                            as:   'secondary_manager',
                            on: {
                              $eq: ['secondary_manager.id', 'agent.secondary_manager_id']
                            }
                          },
                          {
                            join: 's:cities',
                            type: 'left',
                            as:   'city',
                            on: {
                              $eq: ['city.id', 'agent.city_id']
                            }
                          },
                          {
                            join: 's:payment_types',
                            type: 'left',
                            as:   'ptype',
                            on: {
                              $eq: ['ptype.id', 'agent.payment_type_id']
                            }
                          },
                          {
                            join: 's:entities',
                            type: 'left',
                            as:   'ent',
                            on: {
                              $eq: ['ent.id', 'agent.entity_id']
                            }
                          },
                          {
                            join: 's:client_types',
                            type: 'left',
                            as:   'ctype',
                            on: {
                              $eq: ['ctype.id', 'agent.type_id']
                            }
                          }
                        ],
                        where: {
                          $eq: [`agent.id`, {$: params.id}]
                        }
                    })
                ]).then(((result: ServerResult) => {
                    commit('SET_AGENT_DATA', result.getOperation(AGENT_DATA).getContent());

                    if (!params.silence) {
                        commit('LOADED_SUCCESS');
                    }

                    resolve();
                })).catch(((error: any) => {
                    console.error('[Agents.AgentDetails.loadData] ERROR', { params, error });
                    commit('LOADED_ERROR');
                    reject(error);
                }));
            })
        }
    },
    toArchive({ commit }, agent_id) {
      const vm = (this as any)._vm;

      return new Promise((resolve, reject) => {
        vm.$db([
          vm.$q('AGENT_TO_ARCHIVE')
            .update(agent_id, {
              archived_at: moment().format('YYYY-MM-DDTHH:mm:ss')
            })
        ]).then((result: ServerResult) => {
          const operationResult = result.getOperation('AGENT_TO_ARCHIVE');

          if (operationResult.hasError()) {
              reject();
          } else {
              resolve(operationResult.getContent());
          }
        })
        .catch((error: any) => {
            console.error('errror', error)
            reject(error);
        });
      });
  },

  toUnarchive({ commit }, agent_id) {
      const vm = (this as any)._vm;

      return new Promise((resolve, reject) => {
          vm.$db([
              vm.$q('AGENT_TO_ARCHIVE')
                  .update(agent_id, {
                      archived_at: null
                  })
          ]).then((result: ServerResult) => {
              const operationResult = result.getOperation('AGENT_TO_ARCHIVE');

              if (operationResult.hasError()) {
                  reject();
              } else {
                  resolve(operationResult.getContent());
              }
          })
          .catch((error: any) => {
              console.error('errror', error)
              reject(error);
          });
      });
  },
};

export const mutations: MutationTree<AgentDetailsState> = {
    RESET_STATE: (state) => {
        for(const key in initState) {
            (state as any)[key] = initState[key]
        }
    },
    START_LOADING: (state) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },
    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },
    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = true;
    },

    SET_AGENT_DATA(state, data) {
        const schema = data.schema;
        const document = data.documents[0];

        const contactsField = schema.fields.find((field: any) => field.code === 'contacts');
        const primaryContactTypeField = schema.fields.find((field: any) => field.code === 'primary_contact_type');

        if (contactsField) {
            state.meta.contacts = { types: {} };

          for (const _type of contactsField.meta.types) {
            state.meta.contacts.types[_type.value] = _type.label;

            if(_type.type_extras) {
               for(const extraType of _type.type_extras) {
                    state.meta.contacts.types[_type.value + extraType.value] = `${_type.label} / ${extraType.label}`;
                }
            }
          }
        }

        if (primaryContactTypeField) {
            state.meta.primary_contact_types = { choices: {} };

          for (const _type of primaryContactTypeField.meta.choices) {
            state.meta.primary_contact_types.choices[_type.value] = _type.label;
          }
        }

        state.document = document
    },
};

const getters = {

};

export const STORE_KEY = '$_agent_details';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
